import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowUp } from "lucide-react";
import { FormEvent, useRef } from "react";

type ContextOption = {
  value: string;
  label: string;
};

interface InsightsLandingProps {
  query: string;
  selectedContextValue: string;
  contextOptions: { label: string; options: ContextOption[] }[];
  canSubmit: boolean;
  onQueryChange: (value: string) => void;
  onContextChange: (value: string) => void;
  onGenerate: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

export const InsightsLanding = ({
  query,
  selectedContextValue,
  contextOptions,
  canSubmit,
  onQueryChange,
  onContextChange,
  onGenerate,
}: InsightsLandingProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const hasContextOptions = contextOptions.length > 0;

  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        <div className="shrink-0" />
        <div className="text-center">
          <h2 className="text-2xl font-normal">Dig deep into your assignments.</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Query submissions, analyze performance and review feedback.
          </p>
        </div>
      </div>

      <div className="mx-auto">
        <div>
        <form
          ref={formRef}
          className="mb-10 w-[600px] max-w-full rounded-xl shadow-xl px-4 py-2 border"
          onSubmit={onGenerate}
        >
          <Textarea
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.metaKey && canSubmit) {
                e.preventDefault();
                formRef.current?.requestSubmit();
              }
            }}
            placeholder="Ask any question"
            className="w-full resize-none border-0 bg-transparent px-0 py-0 text-base shadow-none focus-visible:ring-0"
          />

          <div className="mt-4 flex items-center justify-between gap-3">
            <Select
              value={selectedContextValue}
              onValueChange={onContextChange}
              disabled={!hasContextOptions}
            >
              <SelectTrigger
                size="sm"
                className="border-0 w-auto px-0 shadow-none focus-visible:ring-0 text-xs text-muted-foreground"
              >
                <SelectValue placeholder="Auto" />
              </SelectTrigger>
              <SelectContent>
                {contextOptions.map((group) => (
                  <SelectGroup key={group.label}>
                    <SelectLabel>{group.label}</SelectLabel>
                    {group.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>

            <Button size="sm" type="submit" disabled={!canSubmit}>
              Send
              <ArrowUp/>
            </Button>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};
